import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Photo from '@/components/base/Photo';

gsap.registerPlugin(ScrollTrigger);

const ENABLE_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';

interface Props {
  src: string;
  alt: string;
  /** Element whose width the photo should match before scrolling (the title box). */
  alignRef: RefObject<HTMLElement | null>;
}

/**
 * Page-hero photo that starts as wide as the title box and expands to its
 * full size as the user scrolls. Uses a clip-path mask only, so layout never
 * moves. Desktop + motion-allowed only; otherwise renders the static photo.
 */
export default function ScrollExpandPhoto({ src, alt, alignRef }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Layout effect: the narrow starting clip is applied before the first paint,
  // so the photo never flashes full-width and then shrinks on page entry.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const mm = gsap.matchMedia();
    mm.add(ENABLE_QUERY, () => {
      const headerH = () => document.querySelector('header')?.getBoundingClientRect().height ?? 72;
      const startClip = () => {
        const align = alignRef.current;
        if (!align) return 'inset(0px 0px 0px 0px round 20px)';
        const p = wrap.getBoundingClientRect();
        const a = align.getBoundingClientRect();
        const l = Math.max(0, Math.round(a.left - p.left));
        const r = Math.max(0, Math.round(p.right - a.right));
        return `inset(0px ${r}px 0px ${l}px round 20px)`;
      };

      // Start narrow immediately.
      gsap.set(wrap, { clipPath: startClip() });

      let tween: gsap.core.Tween | null = null;
      // Create the scroll tween only after the route's scroll-to-top has run,
      // so the scrub never animates from the previous page's scroll position.
      const raf = window.requestAnimationFrame(() => {
        tween = gsap.fromTo(
        wrap,
        { clipPath: startClip },
        {
          clipPath: 'inset(0px 0px 0px 0px round 20px)',
          ease: 'power1.inOut',
          scrollTrigger: {
            trigger: wrap,
            start: 0,
            end: () => `top top+=${headerH()}`,
            scrub: 0.5,
            invalidateOnRefresh: true,
          },
        },
      );
      });

      return () => {
        window.cancelAnimationFrame(raf);
        tween?.scrollTrigger?.kill();
        tween?.kill();
      };
    });

    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) ScrollTrigger.refresh();
    });

    return () => {
      alive = false;
      mm.revert();
    };
  }, [alignRef]);

  return (
    <div className="px-3 md:px-6 lg:px-8">
      <div ref={wrapRef} className="mx-auto max-w-[1376px] w-full will-change-[clip-path]">
        <Photo
          eager
          className="w-full h-[420px] md:h-[680px] rounded-2xl md:rounded-[20px]"
          src={src}
          alt={alt}
        />
      </div>
    </div>
  );
}